
import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { isSuperUser, getDeptCodeByEmail } from "../config/roles";
import { getDeptNameByCode, DEPARTMENTS } from "../constants/departments";
import { 
    listComplianceCards, 
    seedInitialProjectCards, 
    createComplianceCard,
    deleteComplianceCard,
    getLatestCrawlerRun,
    runComplianceCrawlerNow,
    refreshSeedCards
} from "../services/complianceService";
import { ComplianceCard } from "../components/compliance/ComplianceCard";
import { TrackedAuthoritiesDialog } from "../components/compliance/TrackedAuthoritiesDialog";
import { RISK_SCORE_STANDARD } from "../config/governancePolicy";
import { ComplianceCard as IComplianceCard, CrawlerRunLog, ComplianceCardStatus } from "../types/compliance";
import { useToast } from "../components/Toast";
import { Modal } from "../components/Modal";
import { useAiContext } from "../ai/AiContext";

function CrawlerStatusBanner({ isSuper }: { isSuper: boolean }) {
    const [log, setLog] = useState<CrawlerRunLog | null>(null);
    const [running, setRunning] = useState(false);
    const { showToast } = useToast();

    const load = () => getLatestCrawlerRun().then(setLog);

    useEffect(() => { load(); }, []);

    const handleRunNow = async () => {
        if (!confirm("確定要立即執行爬蟲嗎？此操作約需 1-2 分鐘。")) return;
        setRunning(true);
        try {
            await runComplianceCrawlerNow();
            showToast("爬蟲執行完成");
            load();
        } catch (e: any) {
            showToast("執行失敗: " + e.message, "error");
        } finally {
            setRunning(false);
        }
    };

    if (!log) return null;

    const statusColor = log.status === 'SUCCESS' ? '#10b981' : log.status === 'FAILED' ? '#ef4444' : '#fbbf24';
    const timeStr = log.startedAt?.toDate ? log.startedAt.toDate().toLocaleString() : 'N/A';

    return (
        <div style={{marginBottom: 16, padding: '10px 16px', background: '#161616', border: '1px solid #333', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <div style={{fontSize: 12, color: '#ccc'}}>
                <span style={{color: '#aaa', marginRight: 8}}>每日 08:30 排程狀態</span>
                <span style={{fontWeight: 'bold', color: statusColor, marginRight: 12}}>{log.status}</span>
                <span style={{marginRight: 12}}>Last Run: {timeStr}</span>
                <span>(Fetched: {log.fetchedCount} / Created: {log.createdCardsCount})</span>
                {log.errorMessage && <div style={{color: '#ef4444', marginTop: 4}}>Error: {log.errorMessage}</div>}
            </div>
            {isSuper && (
                <button 
                    className="smallbtn" 
                    onClick={handleRunNow} 
                    disabled={running}
                    style={{fontSize: 11, padding: '4px 8px'}}
                >
                    {running ? "執行中..." : "立即執行爬蟲 (測試)"}
                </button>
            )}
        </div>
    );
}

// Compact Grid Card Component
const ProjectGridItem = ({ card, onClick, onDelete, canManage, isOwner }: { card: IComplianceCard, onClick: () => void, onDelete?: ()=>void, canManage: boolean, isOwner: boolean }) => {
    const riskColor = card.riskLevel === 'HIGH' ? '#ef4444' : card.riskLevel === 'MEDIUM' ? '#f59e0b' : '#10b981';
    
    // Check for link/files
    const hasLink = !!card.sectionA.announcementUrl;
    const attachmentCount = (card.sectionA.attachments?.length || 0) + (card.sectionB.attachments?.length || 0);

    // Status color
    const getStatusColor = (s: ComplianceCardStatus) => {
        if (s === 'DRAFT') return '#666';
        if (s === 'IN_REVIEW') return '#fbbf24';
        if (s === 'APPROVED' || s === 'COMPLETED') return 'var(--ok)';
        if (s === 'REJECTED' || s === 'RETURNED') return 'var(--danger)';
        return '#aaa';
    };

    const isEditableStatus = card.status === 'DRAFT' || card.status === 'RETURNED' || card.status === 'NOT_STARTED';
    const showEdit = canManage || (isOwner && isEditableStatus);
    const showDelete = canManage;

    // Data Display
    const dateDisplay = card.sectionA.dateText || card.sectionA.date || "—";
    const agencyDisplay = card.agencyName || "—"; // Use agencyName from card (populated by service)
    const displayRegulatorName = card.agencyName || "主管機關：—";
    
    // Resolve owner display name
    // Try to get name if stored as code, or just use value if it's already a name
    let ownerRaw = card.sectionB.ownerDept || card.ownerDept;
    
    // Attempt to map code to name
    let ownerDisplay = getDeptNameByCode(ownerRaw || "");
    
    // If empty, show suggestion text if available
    if (!ownerDisplay && card.sectionB.ownerDeptSuggestionText) ownerDisplay = "待指定";
    if (!ownerDisplay) ownerDisplay = "待指定";

    return (
        <div 
            style={{
                background: '#1e1e1e', 
                border: '1px solid #333', 
                borderRadius: 12, 
                padding: 16, 
                display: 'flex', 
                flexDirection: 'column',
                justifyContent: 'space-between',
                transition: 'transform 0.2s, border-color 0.2s',
                minHeight: 180,
                position: 'relative'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#555'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#333'; e.currentTarget.style.transform = 'translateY(0)'; }}
        >
            <div onClick={onClick} style={{cursor: 'pointer', flex: 1}}>
                <div style={{display:'flex', justifyContent:'space-between', marginBottom: 8}}>
                    <span className="badge" style={{color: riskColor, borderColor: riskColor, fontSize: 10, padding:'2px 6px'}}>
                        {card.riskLevel}
                    </span>
                    <span className="badge" style={{background: '#333', fontSize: 10, border:'none', maxWidth: 120, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
                        {agencyDisplay}
                    </span>
                </div>
                
                <h3 style={{fontSize: 15, fontWeight: 'bold', color: '#fff', marginBottom: 6, lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'}}>
                    {card.sectionA.title}
                </h3>
                
                <div style={{fontSize: 12, color: '#888', display:'flex', flexDirection:'column', gap: 4}}>
                    <div style={{color: '#a5b4fc'}}>主管機關：{displayRegulatorName}</div>
                    <div style={{color:'#ccc'}}>主責：{ownerDisplay}</div>
                    <div>公告：{dateDisplay}</div>
                    <div style={{display:'flex', gap: 6, alignItems:'center'}}>
                        狀態：<span style={{color: getStatusColor(card.status)}}>{card.status}</span>
                    </div>
                </div>
            </div>

            <div style={{marginTop: 12, paddingTop: 12, borderTop: '1px dashed #333', display:'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <div style={{display:'flex', gap: 10, fontSize: 12, color: '#666'}}>
                    {hasLink && <span title="有公告連結">🔗</span>}
                    {attachmentCount > 0 && <span title="有附件">📎 {attachmentCount}</span>}
                </div>
                <div style={{display:'flex', gap:6}}>
                    {showEdit && (
                        <button className="smallbtn" style={{padding:'2px 8px', fontSize:11}} onClick={onClick}>
                            詳情
                        </button>
                    )}
                    {showDelete && onDelete && (
                        <button className="smallbtn" style={{padding:'2px 8px', fontSize:11, color:'var(--danger)', borderColor:'var(--danger)'}} onClick={(e) => { e.stopPropagation(); onDelete(); }}>
                            刪除
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export function ComplianceControlCenterPage() {
    const { user } = useAuth();
    const { showToast } = useToast();
    const { requestDelete } = useAiContext();
    
    const [activeTab, setActiveTab] = useState<'INTEL' | 'PROJECT' | 'HISTORY'>('PROJECT');
    const [cards, setCards] = useState<IComplianceCard[]>([]);
    const [loading, setLoading] = useState(false);
    const [showCrawlerConfig, setShowCrawlerConfig] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    
    const [selectedCard, setSelectedCard] = useState<IComplianceCard | null>(null);

    const isSuper = isSuperUser(user?.email);
    const userDeptCode = getDeptCodeByEmail(user?.email);
    // User Dept Name is not used for owner check anymore, we rely on Code
    // const userDeptName = getDeptNameByCode(userDeptCode);

    useEffect(() => {
        seedInitialProjectCards().then(() => fetchData());
    }, []);

    useEffect(() => {
        fetchData();
    }, [activeTab]);

    const fetchData = async () => {
        setLoading(true);
        try {
            if (activeTab === 'HISTORY') {
                const list = await listComplianceCards(undefined, true);
                setCards(list);
            } else if (activeTab === 'INTEL') {
                const list = await listComplianceCards('intel', false);
                setCards(list);
            } else {
                const list = await listComplianceCards('project', false);
                setCards(list);
            }
        } catch(e) { console.error(e); } 
        finally { setLoading(false); }
    };

    const handleCreateProject = async () => {
        // Default Owner is Creator's Dept Code
        const id = await createComplianceCard({
            type: 'project',
            status: 'DRAFT',
            riskLevel: 'LOW',
            ownerDept: userDeptCode, 
            sectionA: { 
                title: "新專案 (請填寫)", 
                date: null,
                announcementDate: null, 
                subject: "", 
                summary: "", 
                sourceUrl: "", 
                announcementUrl: "", 
                legalBasis: [], 
                attachments: [] 
            },
            sectionB: { ownerDept: userDeptCode, content: "", attachments: [] },
            coAssistSections: []
        });
        fetchData();
    };

    const handleDelete = (card: IComplianceCard) => {
        if (card.status === 'IN_REVIEW') {
            alert("流程進行中不可刪除");
            return;
        }

        requestDelete({
            targetType: 'complianceCard',
            targetId: card.id,
            summary: card.sectionA.title,
            onConfirm: async () => {
                try {
                    await deleteComplianceCard(card.id, user);
                    showToast("已刪除專案");
                    fetchData();
                } catch(e: any) {
                    showToast(e.message, "error");
                }
            }
        });
    };

    const handleRefreshSource = async () => {
        if(!confirm("確定要更新 9 大義務卡片的最新公告資訊嗎？\n此操作僅會更新 A 段資訊，不會覆蓋您的擬辦內容與流程。")) return;
        setRefreshing(true);
        try {
            const count = await refreshSeedCards();
            showToast(`已更新 ${count} 筆卡片資訊`);
            fetchData();
        } catch(e: any) {
            showToast("更新失敗: " + e.message, "error");
        } finally {
            setRefreshing(false);
        }
    };

    return (
        <div style={{height: '100%', display: 'flex', flexDirection: 'column'}}>
            <div style={{marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <div>
                    <h2 style={{margin:0, fontSize: 20, color: '#fff'}}>法遵情資與專案管控中心</h2>
                </div>
                <div style={{display:'flex', gap: 8}}>
                    <button className="smallbtn" onClick={() => setShowCrawlerConfig(true)}>
                        ⚙️ 管理主管機關
                    </button>
                </div>
            </div>
            
            {activeTab === 'INTEL' && <CrawlerStatusBanner isSuper={isSuper} />}

            <div className="row" style={{borderBottom: '1px solid #333', marginBottom: 16}}>
                <button className={`navbtn ${activeTab==='INTEL'?'active':''}`} onClick={()=>setActiveTab('INTEL')}>法遵情資</button>
                <button className={`navbtn ${activeTab==='PROJECT'?'active':''}`} onClick={()=>setActiveTab('PROJECT')}>專案管控</button>
                <button className={`navbtn ${activeTab==='HISTORY'?'active':''}`} onClick={()=>setActiveTab('HISTORY')}>歷史彙編</button>
            </div>

            {(activeTab === 'INTEL' || activeTab === 'PROJECT') && (
                <div style={{marginBottom: 16, fontSize: 12, color: '#666', background: '#111', padding: 10, borderRadius: 6}}>
                    <span style={{color: '#fff', marginRight: 10}}>風險評分標準：</span>
                    {RISK_SCORE_STANDARD.levels.map(l => (
                        <span key={l.label} style={{marginRight: 12, color: l.color}}>{l.label}</span>
                    ))}
                </div>
            )}

            <div style={{flex: 1, overflowY: 'auto', paddingBottom: 40}}>
                {loading && <div className="p" style={{textAlign:'center', padding:20}}>載入中...</div>}
                
                {!loading && activeTab === 'PROJECT' && isSuper && (
                    <div style={{marginBottom: 16, display:'flex', justifyContent:'space-between'}}>
                         <button 
                             className="btn" 
                             style={{width:'auto'}} 
                             onClick={handleRefreshSource}
                             disabled={refreshing}
                         >
                             {refreshing ? "更新中..." : "更新資訊"}
                         </button>
                         <button className="btn" style={{width:'auto'}} onClick={handleCreateProject}>＋ 新增專案</button>
                    </div>
                )}

                {/* Grid View */}
                {!loading && (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                        gap: 20
                    }}>
                        {cards.map(card => {
                            // Map ownerDept to code for comparison if needed, or rely on logic inside Item
                            const ownerCode = DEPARTMENTS.find(d => d.name === card.ownerDept || d.code === card.ownerDept)?.code || card.ownerDept;
                            const isOwner = ownerCode === userDeptCode || card.sectionB.ownerDept === userDeptCode; // Simplified check
                            
                            return (
                                <ProjectGridItem 
                                    key={card.id} 
                                    card={card} 
                                    onClick={() => setSelectedCard(card)} 
                                    onDelete={() => handleDelete(card)}
                                    canManage={isSuper}
                                    isOwner={isOwner}
                                />
                            );
                        })}
                    </div>
                )}
                
                {!loading && cards.length === 0 && (
                    <div className="p" style={{textAlign:'center', padding: 40}}>無資料</div>
                )}
            </div>

            {/* Detail Modal */}
            {selectedCard && (
                <Modal title={`專案詳情：${selectedCard.sectionA.title}`} onClose={() => setSelectedCard(null)} width={1000}>
                    <ComplianceCard 
                        card={selectedCard} 
                        onRefresh={() => { 
                            fetchData(); 
                            // Re-fetch the specific card from state to update view if possible
                            // Or simpler: close and let user re-open.
                            // Better UX: keep open but we need fresh data. 
                            // Since fetchData updates 'cards', we find it again.
                            // But here we rely on onRefresh call to just update list, modal might stay stale if we don't update selectedCard.
                            // Quick fix: Close modal to force refresh from list.
                            setSelectedCard(null); 
                        }} 
                        readOnly={activeTab === 'HISTORY'} 
                    />
                </Modal>
            )}

            {showCrawlerConfig && (
                <TrackedAuthoritiesDialog onClose={() => setShowCrawlerConfig(false)} onUpdate={()=>{}} />
            )}
        </div>
    );
}

